import React, { useState } from "react";
import {
  PATHS,
  LAND_DEAL,
  RENTAL_PROPERTY,
  HOUSEHACK_PROPERTY,
  CREDIT_TIERS,
  DOWN_PAYMENT_OPTIONS,
  mortgagePayment,
} from "./data.js";

const ACCENT = {
  lime: { border: "border-lime", bg: "bg-lime/10", text: "text-lime", solid: "bg-lime text-ink" },
  cyan: { border: "border-cyan", bg: "bg-cyan/10", text: "text-cyan", solid: "bg-cyan text-ink" },
  magenta: { border: "border-magenta", bg: "bg-magenta/10", text: "text-magenta", solid: "bg-magenta text-ink" },
};

function formatMoney(n) {
  return Math.round(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function PlanningStage({ onDeploy }) {
  const [pathId, setPathId] = useState(null);

  // Land
  const [offer, setOffer] = useState(8000);

  // Rental
  const [creditTierId, setCreditTierId] = useState(null);
  const [downPaymentPct, setDownPaymentPct] = useState(20);

  // House hack
  const [committed, setCommitted] = useState(false);

  const path = PATHS.find((p) => p.id === pathId);
  const accent = path ? ACCENT[path.accent] : null;

  const offerInRange = offer >= LAND_DEAL.offerMin && offer <= LAND_DEAL.offerMax;

  const creditTier = CREDIT_TIERS.find((t) => t.id === creditTierId);
  const rentalLoan = RENTAL_PROPERTY.price * (1 - downPaymentPct / 100);
  const rentalMortgage = creditTier ? mortgagePayment(rentalLoan, creditTier.rate) : null;
  const rentalCashFlow = creditTier ? RENTAL_PROPERTY.marketRent - RENTAL_PROPERTY.fixedMonthlyCosts - rentalMortgage : null;

  const hhLoan = HOUSEHACK_PROPERTY.price * (1 - HOUSEHACK_PROPERTY.downPaymentPct / 100);
  const hhMortgage = mortgagePayment(hhLoan, HOUSEHACK_PROPERTY.rate);
  const hhMonthlyCost = hhMortgage + HOUSEHACK_PROPERTY.fixedMonthlyCosts - HOUSEHACK_PROPERTY.otherUnitRent;

  let canDeploy = false;
  let plan = null;

  if (pathId === "land") {
    canDeploy = offerInRange;
    plan = { path: "land", offer };
  } else if (pathId === "rental") {
    canDeploy = !!creditTierId;
    plan = { path: "rental", creditTierId, downPaymentPct };
  } else if (pathId === "househack") {
    canDeploy = committed;
    plan = { path: "househack" };
  }

  const handleDeploy = () => {
    if (!canDeploy || !plan) return;
    onDeploy(plan);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>CHOOSE YOUR PATH</span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Three ways in. Pick one — each plays out completely differently from here.
      </p>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        {PATHS.map((p) => {
          const a = ACCENT[p.accent];
          const selected = pathId === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setPathId(p.id)}
              className={`border-2 p-4 text-left transition-colors ${selected ? `${a.border} ${a.bg}` : "border-mist/20 hover:border-mist/50"}`}
            >
              <span className="mb-1 block text-2xl">{p.icon}</span>
              <span className={`block font-display text-[10px] uppercase ${selected ? a.text : "text-mist"}`}>{p.label}</span>
              <span className="mt-1 block text-[11px] text-mist/50">{p.pitch}</span>
            </button>
          );
        })}
      </div>

      {pathId === "land" && (
        <section className="panel mb-6 p-4">
          <p className={`mb-2 font-display text-[10px] ${accent.text}`}>YOUR OFFER</p>
          <p className="mb-3 text-[11px] text-mist/50">
            Market value: {formatMoney(LAND_DEAL.marketValue)}. Rule of thumb: offer 25–35% of
            value — that's {formatMoney(LAND_DEAL.offerMin)} to {formatMoney(LAND_DEAL.offerMax)}.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setOffer((o) => Math.max(1000, o - 250))}
              className="h-8 w-8 border-2 border-black bg-panel2 font-display text-xs text-mist"
            >
              −
            </button>
            <span className={`font-display text-lg ${offerInRange ? "text-lime" : "text-danger"}`}>{formatMoney(offer)}</span>
            <button
              onClick={() => setOffer((o) => o + 250)}
              className="h-8 w-8 border-2 border-black bg-panel2 font-display text-xs text-mist"
            >
              +
            </button>
          </div>
          {!offerInRange && (
            <p className="mt-3 text-center text-[11px] text-danger">
              Outside the 25–35% range — too low might insult a motivated seller, too high erases
              your margin.
            </p>
          )}
        </section>
      )}

      {pathId === "rental" && (
        <>
          <section className="panel mb-4 p-4">
            <p className={`mb-2 font-display text-[10px] ${accent.text}`}>CREDIT SCORE</p>
            <p className="mb-3 text-[11px] text-mist/50">Higher scores unlock lower interest rates.</p>
            <div className="grid grid-cols-3 gap-2">
              {CREDIT_TIERS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setCreditTierId(t.id)}
                  className={`border-2 px-2 py-2 text-center font-display text-[9px] transition-colors ${
                    creditTierId === t.id ? accent.solid : "border-mist/30 text-mist/70 hover:border-cyan"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </section>

          <section className="panel mb-6 p-4">
            <p className={`mb-2 font-display text-[10px] ${accent.text}`}>DOWN PAYMENT</p>
            <div className="grid grid-cols-2 gap-2">
              {DOWN_PAYMENT_OPTIONS.map((pct) => (
                <button
                  key={pct}
                  onClick={() => setDownPaymentPct(pct)}
                  className={`border-2 px-3 py-2 font-display text-[10px] transition-colors ${
                    downPaymentPct === pct ? accent.solid : "border-mist/30 text-mist/70 hover:border-cyan"
                  }`}
                >
                  {pct}%
                </button>
              ))}
            </div>

            <div className="mt-4 space-y-1 border-t border-black/40 pt-3 text-[11px]">
              <div className="flex justify-between text-mist/60">
                <span>Purchase Price</span>
                <span>{formatMoney(RENTAL_PROPERTY.price)}</span>
              </div>
              <div className="flex justify-between text-mist/60">
                <span>Market Rent</span>
                <span className="text-lime">{formatMoney(RENTAL_PROPERTY.marketRent)}/mo</span>
              </div>
              {creditTier && (
                <>
                  <div className="flex justify-between text-mist/60">
                    <span>Est. Mortgage @ {creditTier.rate}%</span>
                    <span>{formatMoney(rentalMortgage)}/mo</span>
                  </div>
                  <div className="flex justify-between border-t border-black/40 pt-1 font-display text-[10px]">
                    <span className="text-mist/60">Projected Cash Flow</span>
                    <span className={rentalCashFlow >= 0 ? "text-lime" : "text-danger"}>{formatMoney(rentalCashFlow)}/mo</span>
                  </div>
                </>
              )}
            </div>
          </section>
        </>
      )}

      {pathId === "househack" && (
        <section className="panel mb-6 p-4">
          <p className={`mb-2 font-display text-[10px] ${accent.text}`}>THE NUMBERS</p>
          <div className="mb-4 space-y-1 text-[11px]">
            <div className="flex justify-between text-mist/60">
              <span>Purchase Price</span>
              <span>{formatMoney(HOUSEHACK_PROPERTY.price)}</span>
            </div>
            <div className="flex justify-between text-mist/60">
              <span>Down Payment (FHA)</span>
              <span>{HOUSEHACK_PROPERTY.downPaymentPct}%</span>
            </div>
            <div className="flex justify-between text-mist/60">
              <span>Other Unit's Rent</span>
              <span className="text-lime">{formatMoney(HOUSEHACK_PROPERTY.otherUnitRent)}/mo</span>
            </div>
            <div className="flex justify-between border-t border-black/40 pt-1 font-display text-[10px]">
              <span className="text-mist/60">Your Monthly Housing Cost</span>
              <span className={hhMonthlyCost <= 0 ? "text-lime" : "text-gold"}>{formatMoney(hhMonthlyCost)}/mo</span>
            </div>
            <div className="flex justify-between text-mist/50">
              <span>vs. renting a similar unit elsewhere</span>
              <span>{formatMoney(HOUSEHACK_PROPERTY.comparisonRent)}/mo</span>
            </div>
          </div>

          <button
            onClick={() => setCommitted((c) => !c)}
            className={`flex w-full items-center justify-between border-2 px-3 py-2 text-left transition-colors ${
              committed ? `${accent.border} ${accent.bg}` : "border-mist/20"
            }`}
          >
            <span className="text-[11px] text-mist/80">
              FHA loans require living here at least one year — I understand and commit to that.
            </span>
            <span className={`font-display text-[9px] ${committed ? accent.text : "text-mist/30"}`}>{committed ? "✔" : "—"}</span>
          </button>
        </section>
      )}

      {pathId && (
        <button onClick={handleDeploy} disabled={!canDeploy} className="btn-magenta w-full disabled:opacity-30">
          {canDeploy ? "COMMIT TO THIS DEAL ▶" : "COMPLETE THE PLAN TO COMMIT"}
        </button>
      )}
    </div>
  );
}
